import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { catchError, filter, map, mergeMap, Observable, of, take, timeout } from 'rxjs';
import { AuthService } from '@core/services/auth.service';
import { Store } from '@ngxs/store';
import { AuthStateModel } from 'app/auth/store/auth.state.model';
import { AuthActions } from 'app/auth/store/auth.actions';

@Injectable({
  providedIn: 'root'
})

export class AdminGuardService implements CanActivate{
  constructor(
    private authService: AuthService,
    private router: Router,
    private store: Store
  ) { }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    if (!this.authService.tokenIsExpired()) {
      return this.store.select((state: { auth: AuthStateModel }) => ({
        globalPermissionLoaded: (JSON.parse(localStorage.getItem('userPermissions') || '[]')).length > 0,
        userPermissionsLoaded: (JSON.parse(localStorage.getItem('userPermissions') || '[]')).length > 0,
      })).pipe(
        take(1),
        mergeMap(({ globalPermissionLoaded, userPermissionsLoaded }) => {
          if (globalPermissionLoaded && userPermissionsLoaded) {
            return of(true); // If permissions are already loaded, allow navigation.
          }

          // Dispatch LoadPermissions action if permissions are not loaded.
          return this.store.dispatch(new AuthActions.LoadPermissions()).pipe(
            mergeMap(() => this.waitForPermissionsToLoad()),
            timeout(5000), // timeout to avoid hanging indefinitely.
            map(() => true), // Allow navigation after permissions are loaded.
            catchError(() => {
              this.router.navigate(['/auth/login']); // Redirect to login on failure.
              return of(false);
            })
          );
        })
      );
    } else {
      // Redirect to login page if not logged in as admin
      return this.router.parseUrl('/auth/login');
    }
  }

  private waitForPermissionsToLoad(): Observable<boolean> {
    return this.store.select((state: { auth: AuthStateModel }) => ({
      globalPermissionLoaded: state.auth.globalPermissions.length > 0,
      userPermissionsLoaded: state.auth.auth.permissions.length > 0,
    })).pipe(
      filter(({ globalPermissionLoaded, userPermissionsLoaded }) => 
        globalPermissionLoaded && userPermissionsLoaded
      ),
      take(1),
      map(() => true)
    );
  }
}

